import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getPhotoLibrary } from "../../redux/thunks/mealsetupThunk";

/* ===============================
   PHOTO LIBRARIES
================================ */

const MealUploadLibraryModal = ({ onClose, onSelect, selectedImages = [] }) => {
  const dispatch = useDispatch();
  const [photoLibraries, setPhotoLibraries] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  useEffect(() => {
    dispatch(getPhotoLibrary())
      .unwrap()
      .then((res) => {
        const data = res.data.mealImageGalleryData;
        const grouped = data.reduce((acc, item) => {
          if (!acc[item.category_master]) acc[item.category_master] = [];
          acc[item.category_master].push({
            id: item.id,
            image: item.image,
            category_master: item.category_master,
          });
          return acc;
        }, {});
        setPhotoLibraries(
          Object.keys(grouped).map((cat) => ({
            category_master: cat,
            images: grouped[cat],
          })),
        );
      });
  }, [dispatch]);

  useEffect(() => {
    if (!selectedImages.length) return;

    setSelectedPhotos((prev) => {
      const initial = [];

      photoLibraries.forEach((category) => {
        category.images.forEach((img) => {
          if (selectedImages.includes(img.id)) {
            initial.push(img);
          }
        });
      });

      return initial;
    });
  }, [photoLibraries, selectedImages]);

  const toggleSection = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const togglePhotoSelection = (photo) => {
    setSelectedPhotos((prev) => {
      const exists = prev.find((p) => p.id === photo.id);
      if (exists) {
        return prev.filter((p) => p.id !== photo.id); // remove
      } else {
        return [...prev, photo]; // add
      }
    });
  };

  const isSelected = (photo) => selectedPhotos.some((p) => p.id === photo.id);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold">Choose Meal Photos</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black text-lg"
          >
            ✕
          </button>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {photoLibraries.map((category, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={category.category_master + "-" + index}
                className="border rounded-xl overflow-hidden"
              >
                {/* Accordion Title */}
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100"
                >
                  <span className="text-sm font-semibold text-gray-800">
                    {category.category_master}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-y-auto ${
                    isOpen
                      ? "max-h-[50vh] opacity-100 p-4"
                      : "max-h-0 opacity-0 p-0"
                  }`}
                >
                  {category.images && category.images.length > 0 ? (
                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                      {category.images.map((photo, i) => (
                        <button
                          key={photo.id ?? `${category.category_master}-${i}`}
                          onClick={() => togglePhotoSelection(photo)}
                          className={`relative group rounded-xl overflow-hidden border transition-all duration-200 w-full aspect-square ${
                            isSelected(photo)
                              ? "border-[#FF6943] ring-2 ring-[#FF6943]"
                              : "border-gray-200 hover:border-[#FF6943]"
                          }`}
                        >
                          <img
                            src={photo.image}
                            alt={`${category.category_master}-${i}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />

                          {/* Tick Overlay */}
                          {isSelected(photo) && (
                            <div className="absolute top-2 right-2 bg-[#FF6943] text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md text-xs font-bold">
                              ✓
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm italic">
                      Data not found
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSelect(selectedPhotos);
              onClose();
            }}
            className="px-4 py-2 bg-[#FF6943] text-white rounded-lg text-sm hover:bg-[#ff8760]"
          >
            Select ({selectedPhotos.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealUploadLibraryModal;
